import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from "react";
import useResponsiveScreen from "../hooks/useResponsiveScreen";

type SidebarBreakpoint = "mobile" | "tablet" | "desktop";

const getSidebarBreakpoint = (width: number): SidebarBreakpoint => {
  if (width <= 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
};

type WorkspaceThreadsContextType = {
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
  activeWorkspaceId: string | null;
  setActiveWorkspaceId: (id: string | null) => void;
  openWorkspaceId: string | null;
  setOpenWorkspaceId: (id: string | null) => void;
  toggleWorkspaceDrawer: (workspaceId: string) => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
};

const WorkspaceThreadsContext = createContext<WorkspaceThreadsContextType>({
  activeThreadId: null,
  setActiveThreadId: () => {},
  activeWorkspaceId: null,
  setActiveWorkspaceId: () => {},
  openWorkspaceId: null,
  setOpenWorkspaceId: () => {},
  toggleWorkspaceDrawer: () => {},
  isSidebarCollapsed: false,
  setIsSidebarCollapsed: () => {},
});

export const WorkspaceThreadsProvider = ({ children }: { children: ReactNode }) => {
  const { width } = useResponsiveScreen();
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const [openWorkspaceId, setOpenWorkspaceId] = useState<string | null>(null);
  const initialBreakpoint = getSidebarBreakpoint(width);
  const [isSidebarCollapsed, setIsSidebarCollapsedState] = useState(initialBreakpoint !== "desktop");
  const desktopCollapsedRef = useRef(false);
  const breakpointRef = useRef<SidebarBreakpoint>(initialBreakpoint);

  const setIsSidebarCollapsed = useCallback(
    (value: boolean) => {
      setIsSidebarCollapsedState(value);
      if (getSidebarBreakpoint(width) !== "mobile") {
        desktopCollapsedRef.current = value;
      }
    },
    [width]
  );

  const toggleWorkspaceDrawer = (workspaceId: string) => {
    if (openWorkspaceId === workspaceId) {
      setOpenWorkspaceId(null);
    } else {
      setOpenWorkspaceId(workspaceId);
    }
  };

  useEffect(() => {
    const nextBreakpoint = getSidebarBreakpoint(width);
    if (nextBreakpoint === breakpointRef.current) {
      return;
    }
    breakpointRef.current = nextBreakpoint;
    if (nextBreakpoint === "desktop") {
      setIsSidebarCollapsedState(desktopCollapsedRef.current);
      return;
    }
    setIsSidebarCollapsedState(true);
  }, [width]);

  return (
    <WorkspaceThreadsContext.Provider
      value={{
        activeThreadId,
        setActiveThreadId,
        activeWorkspaceId,
        setActiveWorkspaceId,
        openWorkspaceId,
        setOpenWorkspaceId,
        toggleWorkspaceDrawer,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
      }}>
      {children}
    </WorkspaceThreadsContext.Provider>
  );
};

export const useWorkspaceThreads = () => {
  const context = useContext(WorkspaceThreadsContext);
  if (!context) {
    throw new Error("useWorkspaceThreads must be used within a WorkspaceThreadsProvider");
  }
  return context;
};
